/**
 * **************************************************************************
 * Constants file
 *
 * Defines all constants used across various pages
 * **************************************************************************
 */

import { Injectable } from '@angular/core';
import { DropDownOption } from '@app/interfaces/IDropDown';
import { ColumnOption } from '@app/model/column.model';
import { environment } from '@app/environments/environment';

@Injectable({
  providedIn: 'root',
})
export class ConstantsService {

  /**
   * Constructor
   */
  constructor() { }

  /**
   * Different cards used in queue manager page
   */
  public QUEUE_MANAGER_CARDS = [
    {
      title: 'Waiting for license',
      description: 'Jobs waiting for license',
      id: 'waitLicense'
    },
    {
      title: 'Waiting for dependency',
      description: 'Jobs waiting for hardware',
      id: 'waitDependency',
    },
    {
      title: 'Workflows dispatched',
      description: 'Waiting for runner allocation',
      id: 'dispatched',
    },
    {
      title: 'Running workflows',
      description: 'Jobs in progress',
      id: 'running',
    },
  ];

  public DURATION_DROPDOWN: DropDownOption[] = [
    { "name": "7 days", "value": "7" },
    { "name": "15 days", "value": "15" },
    { "name": "30 days", "value": "30" }
  ];

  public WF_DEFAULT_OPT = [
    { "name": "All", "value": "0" }
  ]

  public FEATURE_WF_CHARTS = [
    { id: 1, tourId: 'deployment', title: "Deployment frequency", value: 20, data: [], color: { domain: ["#86c57a"] }, tooltip: "Frequency of running workflows \n Calculation: Number of workflows run in selected duration" },
    { id: 2, tourId: 'fail', title: "Change failure rate", value: 16.67, data: [], color: { domain: ["#f26c6f"] }, tooltip: "Percentage of changes to production or releases to users result in degraded service. \n Calculation: ((total number of failed workflows / total number of executions) * 100) \n for selected duration" },
    { id: 3, tourId: 'avgLead', title: "Average lead time", value: "16.67", data: [], color: { domain: ["#5eb1f3"] }, tooltip: "The amount of time it takes a commit to get into production \n Calculation: Average (workflow end time - git commit time) \n Currently this is dummy data" },
    { id: 4, tourId: 'avgRestore', title: "Average restore time", value: 16.67, data: [], color: { domain: ["#f4d459"] }, tooltip: "Time taken to recover from a reported failure. \n Calculation: ((action completed time - action created time) / total time) \n Currently this is dummy data" }
  ];

  public DB_DISTRIBUTION = [
    {
      title: 'Workflows executed',
      totalCount: '0',
      data: 'Success rate ',
      id: "wf-executed",
      colorScheme: { domain: ['#1628f0'] },
      graphData: [{ name: "Workflow", series: [] }],
    },
    {
      title: 'Successful',
      totalCount: '0',
      data: 'up by ',
      id: 'successful',
      colorScheme: { domain: ['#4b9e38'] },
      graphData: [{ name: "Workflow", series: [] }]
    },
    {
      title: 'Failed',
      totalCount: '61',
      data: 'down by ',
      id: 'failed', colorScheme: { domain: ['#f56a4e'] },
      graphData: [{ name: "Workflow", series: [] }]
    },
    {
      title: 'Queued',
      totalCount: '7',
      data: '',
      id: 'queued',
      colorScheme: { domain: ['#ebbe2a'] },
      graphData: [{ name: "Workflow", series: [] }]
    },
    {
      title: 'In progress',
      totalCount: '14',
      data: '',
      id: 'inprogress',
      colorScheme: { domain: ['#a860f5'] },
      graphData: [{ name: "Workflow", series: [] }]
    },
    {
      title: 'Aborted',
      totalCount: '0',
      data: '0% -',
      id: 'aborted',
      colorScheme: { domain: ['#FF0000'] },
      graphData: [{ name: "Workflow", series: null }]
    }
  ]

  public OS_NAME = ["Windows", "Linux", "Any"]

  public REPO_URL_REGEX = /^[(http(s)?):\/\/(www\.)?a-zA-Z0-9@:%._\+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_\+.~#?&//=]*)$/
  public STATUS_CODES = {
    unAuthorized: 401
  }

  public pageSizeOptions = [10, 20, 30, 40, 50, 100];

  public QG_CHECK_STATUS_LIST: string[] = ["Success", "Failure", "Neutral", "Cancelled", "Timed_out", "Action_required", "Skipped"];

  /**
   * Scheduler page: table columns, row actions, schedule modes, time options and validation rules
   */
  public SCHEDULER = {
    statusOptions: ['Active', 'Inactive'],
    displayedColumns: ['expand', 'schedulerName', 'description', 'workflow', 'cronExpression', 'nextRun', 'status', 'actions'],
    textColumns: [
      { key: 'schedulerName', header: 'Job Name' },
      { key: 'description', header: 'Description' },
      { key: 'workflow', header: 'Workflow' },
      { key: 'cronExpression', header: 'CRON Expression' },
      { key: 'nextRun', header: 'Next Run' }
    ] as const,
    rowActions: [
      { icon: 'edit', label: 'Edit', permission: 'edit-scheduler' },
      { icon: 'delete', label: 'Delete', permission: 'delete-scheduler' }
    ] as const,
    /** Dynamic Configuration page (menu.json routerLink), linked from each scheduled run */
    dagBuildsRoute: '/dynamic-configs',
    modes: [{ value: 'runOnce', label: 'Run Once' }, { value: 'recurring', label: 'Recurring (CRON)' }] as const,
    executionDetailColumns: [
      { header: 'Workflow Name', key: 'workflowName', sortable: true, style: { all: { 'justify-content': 'left' } } },
      { header: 'Execution Time', key: 'executionTime', sortable: true, style: { all: { 'justify-content': 'left' } } },
      { header: 'Status', key: 'status', sortable: true },
      { header: 'Conclusion', key: 'conclusion', sortable: true },
      { header: 'Build', key: 'buildUrl', sortable: false, style: { all: { 'justify-content': 'left' } } },
      { header: 'Cancel', key: 'cancelRun', sortable: false, style: { all: { 'justify-content': 'left' } } },
      { header: '', key: 'refresh', sortable: false }
    ] as ColumnOption[],
    /** 24-hour HH:mm */
    timePattern: /^([01]?\d|2[0-3]):([0-5]\d)$/,
    /** CRON format expected by the scheduler: min hour dayOfMonth month dayOfWeek */
    cronFieldCount: 5,
    /** Job Executions page: scheduled runs of a day -> Builds (DAG runs) -> Workflows (DAG nodes) */
    jobExecutions: {
      displayedColumns: ['expand', 'workflowName', 'schedulerName', 'executionTime', 'status', 'conclusion', 'action'],
      // 'testReport' (Test Report Link) column is hidden; to unhide, add 'testReport' back before 'action':
      // ['expand', 'sno', 'dag_nm', 'startTime', 'status', 'testReport', 'action']
      buildColumns: ['expand', 'sno', 'dag_nm', 'startTime', 'status', 'action'],
      // Report and Dependency Graph tabs of a build are hidden; to unhide, set the flag to true
      showReportTab: false,
      showDependencyGraphTab: false,
      /** Builds of one scheduled run are listed without a paginator */
      buildsFetchSize: 100,
      workflowColumns: [
        { header: 'Workflow Name', key: 'workflowName', sortable: true, style: { all: { 'justify-content': 'left' } } },
        { header: 'Node Name', key: 'nodeName', sortable: true },
        { header: 'Status', key: 'status', sortable: true },
        { header: 'Conclusion', key: 'conclusion', sortable: true },
        { header: 'Action', key: 'dynamicAction', sortable: false },
        { header: '', key: 'refresh', sortable: false }
      ] as ColumnOption[],
      workflowPageSizeOptions: [5, 10, 20, 30, 100]
    }
  };

  //Sequence should be maintained as its used in NavBar comp and help Comp
  public helpItems = [{
    title: "Dashboard",
    subTitle: "Workflow statuses and summary of the jobs",
    link: '',
    icon: 'analytics',
    label: 'Dashboard Page'
  },
  {
    title: "Feature Workflows",
    subTitle: "Executed workflow results and DORA matrics",
    link: '',
    icon: 'tab',
    label: 'Feature Workflow Page'
  },
  {
    title: "Configuration Builder",
    subTitle: "Configured workflows",
    link: '',
    icon: 'rebase_edit',
    label: 'Configuration Builder Page'
  },
  {
    title: "Workflow Queue Manager",
    subTitle: "Queued jobs that are waiting for dependencies",
    link: '',
    icon: 'loop',
    label: 'Workflow Queue Manager Page'
  },
  {
    title: "GitHub Actions",
    subTitle: "Manually trigger GitHub actions",
    link: '',
    icon: 'play_circle_outline',
    label: 'GitHub Action Page'
  },
  {
    title: "Admin",
    subTitle: "Groups and page access",
    link: '',
    icon: 'admin_panel_settings',
    label: 'Admin Page'
  },
  {
    title: "Tools",
    subTitle: "Tools configuration and license management",
    link: '',
    icon: 'handyman',
    label: 'Tools Page'
  },
  {
    title: "Workflows",
    subTitle: "Summary of configured workflows",
    link: '',
    icon: 'account_tree',
    label: 'Workflow Page'
  }
    ,
  {
    title: "Link",
    subTitle: "Link Page",
    link: '',
    icon: 'open_in_new',
    label: 'Link Page'
  }
  ]
}
